'use client';

import { useRef, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import Image from 'next/image';
import useSWR from 'swr';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { domToBlob } from 'modern-screenshot';
import useGCSUploader from '@/hooks/useGCSUploader';
import { Loader2 } from 'lucide-react';

interface Share2XProps {
  chatId: string;
  query: string;
  shareUrl: string;
}

export default function Share2X(props: Share2XProps) {
  const { query, chatId, shareUrl } = props;
  const ref = useRef<HTMLDivElement>(null);

  const { gcsUpload } = useGCSUploader();
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const handleShare = async () => {
    if (!query) {
      window.open(`https://x.com/intent/post?url=${encodeURIComponent(shareUrl)}`, '_balnk');
      showSuccess(t('Share to X successfully'));
      return;
    }

    if (!ref.current || loading) return;
    setLoading(true);

    try {
      const blob = await domToBlob(ref.current);

      if (!blob) {
        showError(t('Failed to copy image'));
        return;
      }

      const file = new File([blob], chatId, {
        type: 'png/image',
        lastModified: Date.now(),
      });

      const res = await gcsUpload({
        title: query,
        file,
        conversationId: chatId,
      });
      const url = new URL(shareUrl);
      url.searchParams.append('share_id', res?.shareId);
      const searchParams = new URLSearchParams([
        ['url', url.href],
        ['text', 'Here are the key takeaways Pythra search pulled together 👇'],
      ]);
      window.open(`https://x.com/intent/post?${searchParams.toString()}`, '_balnk');
      showSuccess(t('Share to X successfully'));
    } catch (err) {
      console.error('Failed to Share:', err);
      showError(t('Failed to Share'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={handleShare} className="flex flex-col items-center gap-2 group">
        <div className="text-text-primary w-[44px] h-[44px]  bg-bg-main rounded-full flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-[#3d3d3d] transition-colors">
          {loading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <span className=" text-[20px] font-bold">𝕏</span>
          )}
        </div>
        <span className="text-text-secondary text-[11px]">X</span>
      </button>
      {query && (
        <div className="fixed z-100 left-[10000px] top-[10000px] w-[600px] h-[335px] bg-black">
          <div
            ref={ref}
            className="px-4 pb-6 size-full rounded-xl flex flex-col"
            style={{
              background: 'url(/image/bg_x_share.png) bottom center no-repeat',
              backgroundSize: '100%',
            }}
          >
            <div className="flex items-center justify-center gap-2 py-6">
              <Image src="/logo.svg" width={24} height={24} alt="Pythra Research" />
              Pythra
            </div>
            <div
              className="flex flex-1 items-center justify-center font-medium"
              style={{
                fontSize: 'clamp(18px, calc(300px * 0.07), 24px)',
                textAlignLast: 'center',
                lineHeight: 1.5,
              }}
            >
              {query}
            </div>
            {/* <div className="flex items-center justify-between text-[#7a7a7a] mt-5 text-xs">
                <span>{t('Generated by Pythra Research')}</span>
                <div className="flex items-center gap-1">
                  <IAvatar src={userInfo?.avatar ?? ''} name={userInfo?.name ?? ''} size={16} />
                  {userInfo?.name}
                </div>
              </div> */}
          </div>
        </div>
      )}
    </>
  );
}
